"use client";

import { useState } from "react";
import { ChevronLeft, ExternalLink } from "lucide-react";
import { Button } from "@/shadcn-components/ui/button";
import {
  formatPublishedAt,
  ImportanceMark,
  listHeadline,
} from "@/vybe-trader-client/news/newsView";
import type { NewsArticle } from "@/vybe-trader-client/news/stores/useNewsStore";
import { DENSITY } from "@/lib/density-scale";

interface NewsReaderProps {
  /** The currently-FILTERED list — prev/next walk this, never the full catalog. */
  list: NewsArticle[];
  /** Index into `list` of the article being read. */
  index: number;
  /** Navigate to another index within `list` (parent marks read). */
  onNavigate: (index: number) => void;
  /** Pop back to the feed. */
  onClose: () => void;
}

/**
 * Full reader (proposal 65): hero image (degrades to a token gradient when the
 * article has no `imageUrl`), big headline, source + timestamp meta, readable
 * body, external-link affordance, and a PREV/NEXT pager that stays inside the
 * active filter. Safe-area bottom padding mirrors `BottomNavigation.tsx`.
 */
export default function NewsReader({ list, index, onNavigate, onClose }: NewsReaderProps) {
  const article = list[index];
  const [heroFailed, setHeroFailed] = useState(false);

  if (!article) return null;

  const { date, time } = formatPublishedAt(article.publishedAtUnix);
  const prev = index > 0 ? list[index - 1] : null;
  const next = index < list.length - 1 ? list[index + 1] : null;
  const hasHero = Boolean(article.imageUrl) && !heroFailed;

  return (
    <div className="flex h-full min-h-0 flex-col bg-background text-foreground">
      {/* Top bar — back · progress · external link */}
      <div className="flex shrink-0 items-center gap-2 border-b border-border px-2 py-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className={DENSITY.ICON_BTN}
          aria-label="Back to feed"
        >
          <ChevronLeft className="h-4 w-4 text-blue-400" />
        </Button>
        <span className="font-mono text-[11px] text-muted-foreground">Back to feed</span>
        <span className="ml-auto font-mono text-[10px] tabular-nums text-muted-foreground">
          {index + 1} / {list.length}
        </span>
        {article.url && (
          <Button variant="ghost" size="icon" className={DENSITY.ICON_BTN} asChild>
            <a
              href={article.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open original article"
            >
              <ExternalLink className="h-4 w-4 text-muted-foreground" />
            </a>
          </Button>
        )}
      </div>

      {/* Scroll body */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {/* Hero — real image, else a token gradient (never a broken frame) */}
        {hasHero ? (
          <img
            src={article.imageUrl}
            alt=""
            onError={() => setHeroFailed(true)}
            className="h-40 w-full border-b border-border object-cover"
          />
        ) : (
          <div className="flex h-28 items-end border-b border-border bg-gradient-to-br from-blue-400/20 to-orange-500/15 px-3 py-2">
            <div className="flex items-center gap-1.5">
              <ImportanceMark importance={article.importance} isBreaking={article.isBreaking} />
              {article.category && (
                <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[8.5px] uppercase tracking-wide text-muted-foreground">
                  {article.category}
                </span>
              )}
            </div>
          </div>
        )}

        <div className="px-3.5 py-3">
          <h1 className="text-[15px] font-semibold leading-tight tracking-tight text-foreground">
            {article.headline || listHeadline(article)}
          </h1>

          {/* Meta — provider · date · time · sentiment */}
          <div className="mt-2 flex flex-wrap items-center gap-1.5 font-mono text-[9.5px] text-muted-foreground">
            {article.provider && <span className="text-blue-400">◆ {article.provider}</span>}
            <span>·</span>
            <span>{date}</span>
            <span>·</span>
            <span>{time}</span>
            {article.subcategory && (
              <>
                <span>·</span>
                <span>{article.subcategory}</span>
              </>
            )}
            {article.sentiment !== 0 && (
              <>
                <span>·</span>
                <span>
                  sentiment {article.sentiment > 0 ? "+" : ""}
                  {article.sentiment.toFixed(1)}
                </span>
              </>
            )}
          </div>

          {/* Lede / summary */}
          {article.summary && (
            <p className="mt-3 border-t border-border pt-3 text-[12px] font-medium leading-relaxed text-foreground/90">
              {article.summary}
            </p>
          )}

          {/* Body */}
          <p className="mt-2.5 whitespace-pre-wrap text-[11.5px] leading-relaxed text-muted-foreground">
            {article.body || article.summary}
          </p>
        </div>
      </div>

      {/* Prev / next pager — walks the filtered list. No safe-area padding here:
          the reader now sits above `BottomNavigation`, which owns that inset. */}
      <div className="flex shrink-0 gap-2 border-t border-border bg-muted/20 px-2.5 py-2.5">
        <button
          type="button"
          disabled={!prev}
          onClick={() => prev && onNavigate(index - 1)}
          className="flex flex-1 flex-col items-start rounded-lg border border-border bg-muted/40 px-2.5 py-1.5 text-left disabled:opacity-40 active:bg-muted/60"
        >
          <span className="font-mono text-[8px] uppercase tracking-wider text-muted-foreground">
            ‹ Prev
          </span>
          <span className="mt-0.5 line-clamp-1 text-[10px] text-foreground">
            {prev ? listHeadline(prev) : "—"}
          </span>
        </button>
        <button
          type="button"
          disabled={!next}
          onClick={() => next && onNavigate(index + 1)}
          className="flex flex-1 flex-col items-end rounded-lg border border-border bg-muted/40 px-2.5 py-1.5 text-right disabled:opacity-40 active:bg-muted/60"
        >
          <span className="font-mono text-[8px] uppercase tracking-wider text-muted-foreground">
            Next ›
          </span>
          <span className="mt-0.5 line-clamp-1 text-[10px] text-foreground">
            {next ? listHeadline(next) : "—"}
          </span>
        </button>
      </div>
    </div>
  );
}
